import { BASKET_TYPES, selectBasketCanEdit, selectBasketCheckPriceItem, selectBasketRentingSummary } from 'containers/OrderDetails/basketSlice'
import { durationLabel, formatWindowRange, getItemDuration } from 'utils/shortTerm'
import useSummaryData from 'containers/OrderDetails/Summary/hooks/useSummaryData'
import { useHistory, useParams } from 'react-router-dom'
import placeholder from 'assets/img/placeholder.png'
import React, { Fragment, useState } from 'react'
import { LOCATION_STATES } from 'utils/constants'
import useTranslation from 'hooks/useTranslation'
import Checkbox from 'components/inputs/Checkbox'
import parking from 'assets/img/parking.png'
import { displayPrice } from 'utils/string'
import htbox1 from 'assets/img/htbox1.png'
import { useReduxSelector } from 'store'
import { toLongDate } from 'utils/date'
import { v7 } from 'uuid'

type BasketItem = basic.Form
interface ListItemProps extends BasketItem {
  index: number
  deliveries: BasketItem[]
  placeLocks: BasketItem[]
  warehouseLocks: BasketItem[]
  isOnlyOnePositionInTheBasket: boolean
}

const ListItem: React.FC<ListItemProps> = ({ identifier, name, placeAddress, start, end, image, noticePeriod, deliveries, placeLocks, warehouseLocks, type, isOnlyOnePositionInTheBasket, rentableType, isShortTerm, startDateTime, endDateTime, durationMinutes }) => {
  const { id } = useParams<{ id?: string }>()
  const [isExpanded, expand] = useState(isOnlyOnePositionInTheBasket || id === identifier)
  const checkPrice = useReduxSelector(state => selectBasketCheckPriceItem(state, identifier))
  const rentingSummary = useReduxSelector(selectBasketRentingSummary)
  const canEdit = useReduxSelector(selectBasketCanEdit)
  const data = useSummaryData(identifier)

  const isSelected = id === identifier
  const thereAreAvailableWarehouseLocks = (checkPrice?.commonLockInfo?.key === 'full-automatic' || checkPrice?.warehouseLockInfo?.key === 'full-automatic')
    ? false
    : !!checkPrice?.lock?.openingMethods && checkPrice.lock.openingMethods.length > 0
  const thereAreAvailablePlaceLocks = (checkPrice?.commonLockInfo?.key === 'full-automatic' || checkPrice?.placeLockInfo?.key === 'full-automatic')
    ? false
    : !!checkPrice?.placeLock?.gate?.openingMethods && checkPrice.placeLock.gate.openingMethods.length > 0
  const thereAreWarehouseLocksSelected = thereAreAvailableWarehouseLocks ? (warehouseLocks && warehouseLocks.length > 0) : true
  const thereArePlaceLocksSelected = thereAreAvailablePlaceLocks ? (placeLocks && placeLocks.length > 0) : true
  const deliveryPrice = 0

  const { t, lang } = useTranslation('basket')
  const { push, goBack, replace } = useHistory()

  const editWarehouse = () => {
    goBack()
    if (!isSelected) {
      setTimeout(() => {
        replace(`/order/${identifier}`)
      }, 100)
    }
  }

  return (
    <div className='basketPopup__list__item'>
      <div className='basketPopup__list__item__block group'>
        {type === BASKET_TYPES.rent && canEdit !== 0
          ? <Checkbox
            name={v7()}
            checked={isSelected}
            label={t(isSelected ? 'inEditMode' : 'clickToEdit')}
            changeHandler={editWarehouse}
          />
          : <div />}
        <div
          className='basketPopup__list__item__button'
          onClick={() => {
            expand(e => !e)
          }}>
          <span>{t(isExpanded ? 'collapse' : 'expand')}</span>
          <i className={`icon icon--chevron-${isExpanded ? 'up' : 'down'}`} />
        </div>
      </div>

      <div
        className='basketPopup__list__item__block basketPopup__list__item__header'
        onClick={editWarehouse}>
        <img
          alt='warehouse'
          src={(image || (type === BASKET_TYPES.rent ? (rentableType === 'parking' ? parking : htbox1) : placeholder))}
        />
        <div className='basketPopup__list__item__header__title'>
          <div>{name}</div>
          <div>{placeAddress}</div>
          {(!thereArePlaceLocksSelected || !thereAreWarehouseLocksSelected) && <div className='--red'>{t('completeEditing')}!</div>}
        </div>
      </div>

      <div className={`hidableElement --${isExpanded ? 'visible' : 'hidden'}`}>
        <div>
          {type === BASKET_TYPES.rent && <div className='basketPopup__list__item__block'>
            <div className='basketPopup__list__item__row'>
              <div>
                <i className='icon icon--calendar-bold' />
                <span>{t('rentalPeriod')}</span>
              </div>
              <span>{isShortTerm
                ? `${formatWindowRange(startDateTime, durationMinutes, endDateTime, lang)} (${durationLabel(getItemDuration({ startDateTime, durationMinutes, endDateTime }) || 0, lang)})`
                : `${toLongDate(start, lang, true)} - ${end ? toLongDate(end, lang, true) : t('indefinite')}`}</span>
            </div>
            {!isShortTerm && noticePeriod > 0 && <div className='basketPopup__list__item__row'>
              <div>
                <i className='icon icon--terminate-bold' />
                <span>{t('periodOfNotice')}</span>
              </div>
              <span>{noticePeriod} {t(noticePeriod === 1 ? 'day' : 'days')}</span>
            </div>}
            {thereAreAvailablePlaceLocks && <div className='basketPopup__list__item__row'>
              <div>
                <i className='icon icon--lock-bold' />
                <span>{t('yardLock')}</span>
              </div>
              {placeLocks && placeLocks.length > 0
                ? <span>
                  {placeLocks.map(({ name }: BasketItem, idx: number) => `${!idx ? '' : ', '}${name}`)}
                </span>
                : <span className='--red'>{t('none')}</span>}
            </div>}
            {thereAreAvailableWarehouseLocks && <div className='basketPopup__list__item__row'>
              <div>
                <i className='icon icon--lock-bold' />
                <span>{t('warehouseLock')}</span>
              </div>
              {warehouseLocks && warehouseLocks.length > 0
                ? <span>
                  {warehouseLocks.map(({ name }: BasketItem, idx: number) => `${!idx ? '' : ', '}${name}`)}
                </span>
                : <span className='--red'>{t('none')}</span>}
            </div>}
          </div>}

          {data?.monthly && data.monthly.length > 0 && <div className='basketPopup__list__item__block'>
            <div className='basketPopup__list__item__row'>
              <span className='--title'>{t('schedule')}</span>
              <span className='--smaller'>{data.monthly?.[0]?.description}</span>
            </div>
            {data.monthly?.map(({ title, price_sum }, idx) => <div
              key={idx}
              className='basketPopup__list__item__row'>
              <span>{title}</span>
              <span>{displayPrice(price_sum)}</span>
            </div>)}
          </div>}

          <div className='basketPopup__list__item__block'>
            <div className='basketPopup__list__item__row'>
              <span className='--title'>{t('feesNow')}</span>
            </div>
            {data?.now?.list?.map(({ text, price, depositAmount, type }, idx) => {
              return <Fragment key={idx}>
                <div className={`basketPopup__list__item__row${type === 'discount' ? ' --red' : ''}`}>
                  <span>{text}:</span>
                  <span>{displayPrice(depositAmount ? (Number(price) - Number(depositAmount)) : price)}</span>
                </div>
                {depositAmount && <div className='basketPopup__list__item__row'>
                  <span>{t('deposit')}:</span>
                  <span>{displayPrice(depositAmount)}</span>
                </div>}
              </Fragment>
            })}
            {deliveries?.map(item => {
              const price = Number(rentingSummary?.deliveryPrice?.deliveries?.[item.externalDeliveryId]?.grossPrice || 0)
              return <div
                key={item.identifier}
                className='basketPopup__list__item__row'>
                <span>{t('delivery')}{item.deliveryType ? ` (${t(item.deliveryType)})` : ''}:</span>
                <span>{displayPrice(price)}</span>
              </div>
            })}
          </div>
        </div>
      </div>

      <div className='basketPopup__list__item__block'>
        <div className='basketPopup__list__item__row'>
          {canEdit === 0
            ? <span />
            : <span
              className='--button'
              onClick={() => {
                goBack()
                setTimeout(() => {
                  push(window.location.pathname, LOCATION_STATES.popup({ type: 'removeCartPosition', cartId: identifier }))
                }, 100)
              }}>
              <i className='icon icon--trash s14 cPrimary' />
              <span className='--red'>{t('delete')}</span>
            </span>}
          <div className='basketPopup__list__item__sum'>
            <span className='--red'>{t(isExpanded ? 'totalFees' : 'total')}</span>
            <span>{displayPrice(data?.now?.price_sum ? Number(data.now.price_sum) + deliveryPrice : 0)}</span>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ListItem